import { Button, Flex, Modal, Text, UnstyledButton } from '@mantine/core';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import FieldText from '@/components/shared/form/FieldText';
import FieldTextareaWithEditingMode from '@/components/shared/form/FieldTextareaWithEditingMode';
import { PDF_NOTE_COLOR_OPTIONS } from '@/configs/pdfNotes';
import { OrganizationDocumentPreviewPageNotesStore } from '@/stores/local/pages/OrganizationDocumentPreviewPageStore/NotesStore';
import type { PdfNoteHighlightColor } from '@/stores/local/shared/PdfNotesStore';

import s from './DocumentNoteEditorModal.module.scss';

type DocumentNoteEditorModalProps = {
  store: OrganizationDocumentPreviewPageNotesStore;
  isPhone: boolean;
  title: string;
};

const DocumentNoteEditorModal: React.FC<DocumentNoteEditorModalProps> = ({ store, isPhone, title }) => {
  const anchorType = store.pendingSelection?.anchorType ?? 'text';
  const isPointOrArea = anchorType === 'point' || anchorType === 'area';

  const handleColorButtonClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const color = event.currentTarget.dataset.color as PdfNoteHighlightColor;

      if (color) {
        store.setDraftColor(color);
      }
    },
    [store],
  );

  return (
    <Modal
      classNames={{ body: s.root }}
      opened={store.isCreateMode}
      onClose={store.cancelCreate}
      title={title}
      centered={!isPhone}
      fullScreen={isPhone}
      size="lg"
    >
      <Flex direction="column" gap="md" className={s.root__create}>
        <FieldText store={store.draftCaptionField} label="Заголовок" placeholder="Название заметки" />
        <FieldTextareaWithEditingMode
          store={store.draftTextField}
          placeholder={isPointOrArea ? 'Текст заметки (опционально)' : 'Текст заметки'}
          minRows={8}
        />
        <div>
          <Flex direction="column" className={s['root__color-label-container']}>
            <Text size="sm" fw={500}>
              Цвет выделения
            </Text>
            <Text size="xs" c="dimmed">
              (задаёт цвет подсветки выделенного фрагмента на странице)
            </Text>
          </Flex>
          <div className={s['root__color-picker']}>
            {PDF_NOTE_COLOR_OPTIONS.map(({ value }) => (
              <UnstyledButton
                key={value}
                data-color={value}
                className={cn(
                  s['root__color-circle'],
                  s[`root__color-circle_${value}`],
                  store.draftColor === value && s['root__color-circle_active'],
                )}
                onClick={handleColorButtonClick}
              />
            ))}
          </div>
        </div>
        <Flex className={s['root__actions']} justify="flex-end" gap="xs">
          <Button variant="default" onClick={store.cancelCreate}>
            Отмена
          </Button>
          <Button onClick={store.createNote} loading={store.createMeta.isLoading} disabled={!store.canSaveNote}>
            Сохранить
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default observer(DocumentNoteEditorModal);
